/**
 * Date helper utilities for local timezone handling.
 *
 * The database stores date_of_birth as PostgreSQL DATE (timezone-neutral).
 * These utilities ensure that date inputs and defaults use the user's local
 * timezone rather than UTC, preventing edge cases where UTC midnight has
 * rolled to the next day but the user is still on the previous day locally.
 */

/**
 * Returns today's date in YYYY-MM-DD format using the user's local timezone.
 *
 * Example: At 8pm ET on April 24, this returns "2026-04-24"
 * (whereas new Date().toISOString().split('T')[0] would return "2026-04-25")
 */
export function getLocalToday() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Checks if a given date string (YYYY-MM-DD) is in the future relative to
 * today in the user's local timezone.
 */
export function isDateInFuture(dateString) {
  if (!dateString) return false;
  return dateString > getLocalToday();
}

/**
 * Formats an API date value as MM/DD/YYYY for display.
 *
 * Deliberately splits the string instead of going through `new Date(...)`.
 * Postgres DATE columns come back from node-postgres as JS Date objects at the
 * *server's* local midnight, so on Cloud Run (TZ=UTC) the wire value is
 * "2026-08-26T00:00:00.000Z" — and `new Date(that).toLocaleDateString()` in any
 * browser west of Greenwich renders the previous day. Taking the first 10
 * characters reads the calendar date the database actually stores, which is
 * what a date of birth or a due date means.
 *
 * Accepts both "2026-08-26" and "2026-08-26T00:00:00.000Z" — i.e. anything the
 * API sends, which is always a string. Anything else (a Date instance, a
 * malformed value) returns the fallback rather than a half-formatted string.
 */
export function formatDisplayDate(dateString, fallback = 'N/A') {
  if (!dateString) return fallback;
  const datePart = String(dateString).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return fallback;
  const [year, month, day] = datePart.split('-');
  return `${month}/${day}/${year}`;
}
